import axiosInstance from '../axios';
import { getTokenFromLocalStorage, getRefreshFromTokenLocalStorage } from '../../helpers/localStorage';
import { PATHS } from '../../enums/PATHS';
import type { CacheRequestConfig } from 'axios-cache-interceptor';
import { API_ROUTES } from '../../enums/API_ROUTES';
import { FOLDER_CONTENT_FILTER, VIDEOFEED_TYPE } from '../../enums/ENUMS';
import { ACCESS_TYPE_ENUM } from '../../enums/ACCESS_ENUM';
import { SortOptions, SortType } from '../../enums/SORT';

export const getAllVideofeeds = async (data: {
  folderId: string | null;
  page?: number;
  limit?: number;
  search?: string;
  sort?: 'asc' | 'desc';
  sortOption: SortOptions;
  status: string;
  grouped?: boolean;
  signal?: AbortSignal;
}) => {
  const { page = 1, limit = 10, folderId, search = '', sort, sortOption, status, grouped = false, signal } = data;
  const cacheConfig: CacheRequestConfig | undefined = bypassCacheForNextGetAllVideofeedsRequest || grouped ? { cache: false } : undefined;
  bypassCacheForNextGetAllVideofeedsRequest = false;
  const res = await axiosInstance.get(
    `${API_ROUTES.VIDEOFEED}?page=${
      page + 1
    }&limit=${limit}&folderId=${folderId}&search=${search}&sort=${sort}&sortField=${sortOption}&status=${status}&grouped=${grouped}`,
    { signal, ...(cacheConfig || {}) }, // Pass the signal to cancel the request if needed
  );
  return res;
};

export const getFolderContents = async (data: {
  folderId: string;
  page?: number;
  limit?: number;
  search?: string;
  sort?: 'asc' | 'desc';
  sortOption: SortOptions;
  view: 'mine' | 'shared' | 'all-content' | 'collaborative';
  accessType?: ACCESS_TYPE_ENUM | null;
  contentFilter?: FOLDER_CONTENT_FILTER;
  status?: string;
  ownerIds?: string[];
  signal?: AbortSignal;
}) => {
  const {
    folderId,
    page = 0,
    limit = 10,
    search = '',
    sort,
    sortOption,
    view,
    accessType,
    contentFilter,
    status,
    ownerIds,
    signal,
  } = data;

  const res = await axiosInstance.get(
    `${API_ROUTES.VIDEOFEED_FOLDER_CONTENTS}?folderId=${folderId}&page=${
      page + 1
    }&limit=${limit}&search=${search}&sort=${sort}&sortField=${sortOption}&view=${view}${
      accessType ? `&accessType=${accessType}` : ''
    }${
      contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL
        ? `&contentFilter=${contentFilter}`
        : ''
    }${
      status ? `&status=${status}` : ''
    }${
      ownerIds?.length ? `&ownerIds=${ownerIds.join(',')}` : ''
    }`,
    { signal },
  );

  return res.data;
};

export const searchGlobalContent = async (
  search: string,
  limit = 10,
  signal?: AbortSignal,
  allContent = false,
) => {
  const res = await axiosInstance.get(
    `${API_ROUTES.VIDEOFEED_GLOBAL_SEARCH}?search=${encodeURIComponent(search)}&limit=${limit}${allContent ? '&allContent=true' : ''}`,
    { signal },
  );

  return res.data;
};

export const getAllVideofeedsWithoutPagination = async (folderId: string, type?: VIDEOFEED_TYPE, sort?: SortType, sortField?: any) => {
  const typeOfFeed = type ? `&type=${type}` : '';
  const sortTypeQuery = sort ? `&sort=${sort}` : '';
  const sortFieldQuery = sort ? `&sortField=${sortField}` : '';
  const res = await axiosInstance.get(
    `${API_ROUTES.VIDEOFEED}?folderId=${folderId}${typeOfFeed}${sortTypeQuery}${sortFieldQuery}&grouped=true`,
    { cache: false } as CacheRequestConfig,
  );
  return res.data;
};

export const updateVideofeedTitle = async (id: string, title: string, options?: { renameCollectionOnly?: boolean }) => {
  const res = await axiosInstance.patch(`${API_ROUTES.UPDATE_VIDEOFEED_TITLE}/${id}`, {
    title,
    renameCollectionOnly: options?.renameCollectionOnly ?? false,
  });
  return res.data;
};

export const uploadVideofeedThumbnail = async (id: string, data: FormData) => {
  return await axiosInstance.patch(`${API_ROUTES.VIDEOFEED}/thumbnail/${id}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const removeVideofeedThumbnail = async (id: string) => {
  return await axiosInstance.delete(`${API_ROUTES.VIDEOFEED}/thumbnail/${id}`);
};

export const updateVideoTitle = async (id: string, name: string) => {
  const res = await axiosInstance.patch(`${API_ROUTES.UPDATE_VIDEO_TITLE}/${id}`, {
    name,
  });
  return res.data;
};

export const deleteVideoAngle = async (videofeedId: string, videoId: string) => {
  const res = await axiosInstance.delete(
    `${API_ROUTES.VIDEOFEED}/${videofeedId}/angles/${videoId}`,
  );
  return res.data;
};

export const postRefreshVideofeedStatus = async (id: string) => {
  const res = await axiosInstance.post(`${API_ROUTES.VIDEOFEED_REFRESH_STATUS}/${id}`);
  return res.data;
};

let bypassCacheForNextGetAllVideofeedsRequest = false;

export const bypassCacheForNextVideofeedsListFetch = () => {
  bypassCacheForNextGetAllVideofeedsRequest = true;
};

export const updateTopicName = async (
  id: string,
  data: {
    name: string;
  },
) => {
  const res = await axiosInstance.patch(`${API_ROUTES.TOPIC_UPDATE}/${id}`, data);
  return res.data;
};

export const deleteVideofeed = async (data: {
  videofeedId: string;
  confirm?: boolean;
  deleteCollection?: boolean;
}) => {
  const { videofeedId, confirm = false, deleteCollection = false } = data;
  const res = await axiosInstance.delete(
    `${API_ROUTES.VIDEOFEED}/${videofeedId}?confirm=${confirm}&deleteCollection=${deleteCollection}`,
  );
  return res;
};

export const deleteComposerById = async (data: { videofeedId: string }) => {
  const { videofeedId } = data;
  const res = await axiosInstance.delete(`${API_ROUTES.REFLEX_COMPOSER_ALL_CONTENT}/${videofeedId}`);
  return res;
};

export const restoreArchivedVideofeed = async (videofeedId: string | number) => {
  const res = await axiosInstance.patch(
    `${API_ROUTES.VIDEOFEED_RESTORE}/${videofeedId}`,
  );
  return res;
};

export const archiveVideofeedOnly = async (videofeedId: string | number) => {
  const res = await axiosInstance.patch(
    `${API_ROUTES.VIDEOFEED_ARCHIVE_ONLY}/${videofeedId}`,
  );
  return res;
};

export const postRunArchiveBackfill = async () => {
  const res = await axiosInstance.post(API_ROUTES.VIDEOFEED_ARCHIVE_BACKFILL_RUN);
  return res;
};

export const postCreateVideoFeed = async (data: {
  folderId: string;
  videos: { video_id: string; size: number; quality?: number }[];
  upload_type: 'package' | 'standalone';
  title?: string;
  multi_quality?: boolean;
  originalName: string;
}) => {
  const { folderId, videos, upload_type, title, multi_quality, originalName } = data;
  let videofeedTitleTransformed = title;
  let originalNameTransformed = originalName;
  if (typeof videofeedTitleTransformed === 'string' && videofeedTitleTransformed.length > 255) {
    videofeedTitleTransformed = videofeedTitleTransformed.slice(0, 254);
  }
  // clamp to 255 chars (as entity description)
  if (typeof originalNameTransformed === 'string' && originalNameTransformed.length > 255) {
    originalNameTransformed = originalNameTransformed.slice(0, 254);
  }

  const body = {
    folderId,
    title: videofeedTitleTransformed,
    videos,
    upload_type,
    multi_quality,
    original_name: originalNameTransformed,
  };
  if (!title) {
    delete body.title;
  }
  const res = await axiosInstance.post(API_ROUTES.VIDEOFEED, {
    ...body,
  });
  return res;
};

export const postCreateMergeSession = async (data: {
  folderId: number;
  title: string;
  videos: { name?: string; size: number; quality?: number; duration?: number }[];
}) => {
  return await axiosInstance.post(API_ROUTES.MERGE_SESSION, data);
};

export const getMergeSession = async (id: string | number) => {
  return await axiosInstance.get(`${API_ROUTES.MERGE_SESSION}/${id}`);
};

export const postStartMergeSessionUpload = async (
  sessionId: string | number,
  videoId: string | number,
  numberOfParts: number,
) => {
  return await axiosInstance.post(`${API_ROUTES.MERGE_SESSION}/${sessionId}/videos/${videoId}/start-upload`, {
    numberOfParts,
  });
};

export const postCompleteMergeSessionUpload = async (
  sessionId: string | number,
  videoId: string | number,
  data: { uploadId: string; chunks: { PartNumber: number; ETag: string }[] },
) => {
  return await axiosInstance.post(`${API_ROUTES.MERGE_SESSION}/${sessionId}/videos/${videoId}/complete-upload`, data);
};

export const postFinalizeMergeSession = async (id: string | number, outputName?: string) => {
  return await axiosInstance.post(`${API_ROUTES.MERGE_SESSION}/${id}/finalize`, {
    outputName,
  });
};

export const postCancelMergeSession = async (id: string | number) => {
  return await axiosInstance.post(`${API_ROUTES.MERGE_SESSION}/${id}/cancel`);
};

export const patchTriggerTranscoding = async (feedId: string, quality: string[]) => {
  const res = await axiosInstance.patch(`${API_ROUTES.VIDEOFEED}/${feedId}`, {
    quality,
  });
  return res;
};

export const uploadJSON = async (file: File | Blob, fileName: string, bucketName: string) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fileName', fileName);
  formData.append('bucketName', bucketName);

  const res = await axiosInstance.post(`${API_ROUTES.VIDEOFEED_JSON}`, formData);

  return res;
};

export const postAddJsonToVideo = async (feedId: string, jsonSize?: string) => {
  const res = await axiosInstance.post(`${API_ROUTES.VIDEOFEED_STANDALONE}/${feedId}`, {
    jsonSize,
  });
  return res;
};

export const getAllSharedFeeds = async (
  folderId: string,
  sort: SortType,
  sortOption: SortOptions,
  accessType: ACCESS_TYPE_ENUM | null,
  page: number,
  limit: number,
  search: string,
  grouped = false,
  signal?: AbortSignal,
) => {
  const cacheConfig: CacheRequestConfig | undefined = grouped ? { cache: false } : undefined;
  const res = await axiosInstance.get(
    `${API_ROUTES.VIDEOFEED_SHARED_WITH_ME}?folderId=${folderId}&sort=${sort}&sortField=${sortOption}&search=${search}${
      accessType ? `&accessType=${accessType}` : ''
    }${`&page=${page + 1}`}${`&limit=${limit ? limit : 1000}`}${`&grouped=${grouped}`}`,
    { signal, ...(cacheConfig || {}) }, // Pass the signal to cancel the request if needed
  );
  return res.data;
};
export const getAllContentVideofeedsByFolderId = async (
  folderId: string,
  sort: SortType,
  sortOption: SortOptions,
  page: number,
  limit: number,
  search: string,
  accessType?: ACCESS_TYPE_ENUM,
  grouped = false,
) => {
  const cacheConfig: CacheRequestConfig | undefined = grouped ? { cache: false } : undefined;
  const res = await axiosInstance.get(
    `${API_ROUTES.ALL_CONTENT_VIDEOFEEDS}?folderId=${folderId}&sort=${sort}&sortField=${sortOption}${
      accessType ? `&accessType=${accessType}` : ''
    }&page=${page + 1}&limit=${limit}&search=${search}&grouped=${grouped}`,
    { ...(cacheConfig || {}) },
  );
  return res.data;
};

export const checkFolderAccess = async (folderId: string) => {
  const res = await axiosInstance.get(`${API_ROUTES.ACCESS_CHECK}?folderId=${folderId}`);
  return res.data;
};

const cloudFrontCookieScopeCache = new Map<string, number>();
type CloudFrontCookieResponse = { url: string; expiresAt: number; resource: string };
const cloudFrontCookieRequests = new Map<string, { url: string; promise: Promise<CloudFrontCookieResponse>; retryOnFailure: boolean }>();
let cloudFrontRefreshTimeout: number | undefined;

const scheduleCloudFrontCookieRefresh = (url: string, delayMs: number, retryAttempt = 0) => {
  window.clearTimeout(cloudFrontRefreshTimeout);
  cloudFrontRefreshTimeout = window.setTimeout(() => {
    cloudFrontRefreshTimeout = undefined;
    if (!getTokenFromLocalStorage() && !getRefreshFromTokenLocalStorage()) return;
    if (window.location.pathname === PATHS.ADMIN_PAGE.path ||
        window.location.pathname.startsWith(`${PATHS.ADMIN_PAGE.path}/`)) return;
    void issueCloudFrontSignedCookies(url, { forceRefresh: true, retryAttempt }).catch(() => {});
  }, delayMs);
};
let lastCloudFrontCookieScopeKey: string | null = null;
const CLOUDFRONT_FRONTEND_HOSTS = new Set([
  'dev.amplify.site',
  'sydney.amplify.site',
  'sydney-staging.amplify.site',
  'staging-london.amplify.site',
  'france.amplify.site',
  'london.amplify.site',
  'eastcoast.amplify.site',
  'ireland.amplify.site',
  'saopaulo.amplify.site',
  'hongkong.amplify.site',
]);

export const isCloudFrontCookiesEnabledForCurrentHost = (): boolean => {
  if (typeof window === 'undefined') return false;
  return CLOUDFRONT_FRONTEND_HOSTS.has(window.location.hostname);
};

export const shouldUseCredentialsForMediaUrl = (url: string): boolean => {
  if (!isCloudFrontCookiesEnabledForCurrentHost()) return false;

  try {
    const parsed = new URL(
      url,
      typeof window !== 'undefined' ? window.location.origin : undefined,
    );
    return (
      !parsed.hostname.endsWith('.cloudfront.net') &&
      !parsed.pathname.includes('/api/videofeed/media/')
    );
  } catch {
    return !url.includes('cloudfront.net') && !url.includes('/api/videofeed/media/');
  }
};

const shouldBypassCloudFrontCookies = (url: string): boolean =>
  !shouldUseCredentialsForMediaUrl(url);

const getCloudFrontCookieScopeKey = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    return parsed.host;
  } catch {
    return null;
  }
};

export const issueCloudFrontSignedCookies = async (
  url = '',
  options?: { forceRefresh?: boolean; retryAttempt?: number; retryOnFailure?: boolean },
) => {
  if (shouldBypassCloudFrontCookies(url)) {
    return { url, expiresAt: 0, resource: '' };
  }

  if (options?.retryOnFailure === false) {
    window.clearTimeout(cloudFrontRefreshTimeout);
    cloudFrontRefreshTimeout = undefined;
  }

  const now = Math.floor(Date.now() / 1000);
  const scopeKey = getCloudFrontCookieScopeKey(url) || lastCloudFrontCookieScopeKey;
  const cachedExpiresAt = scopeKey ? cloudFrontCookieScopeCache.get(scopeKey) : undefined;

  // Reuse existing signed cookies while still valid (60s safety buffer).
  if (!options?.forceRefresh && cachedExpiresAt && cachedExpiresAt - now > 60) {
    if (cloudFrontRefreshTimeout === undefined) {
      scheduleCloudFrontCookieRefresh(url, Math.max(1000, (cachedExpiresAt - now - 300) * 1000));
    }
    return { url, expiresAt: cachedExpiresAt, resource: '' };
  }

  const requestKey = scopeKey || '';
  let pending = cloudFrontCookieRequests.get(requestKey);
  if (!pending) {
    const request = (async (): Promise<CloudFrontCookieResponse> => {
      try {
        const res = await axiosInstance.post(
          API_ROUTES.VIDEOFEED_CLOUDFRONT_COOKIES,
          { url },
          { withCredentials: true, timeout: 15000 },
        );
        const payload = res.data as CloudFrontCookieResponse;
        const effectiveScopeKey = scopeKey || getCloudFrontCookieScopeKey(payload?.url || '');
        if (!payload?.expiresAt) throw new Error('Missing CloudFront cookie expiry');
        if (effectiveScopeKey) {
          lastCloudFrontCookieScopeKey = effectiveScopeKey;
          cloudFrontCookieScopeCache.set(effectiveScopeKey, payload.expiresAt);
        }
        scheduleCloudFrontCookieRefresh(
          payload.url || url,
          Math.max(1000, (payload.expiresAt - Math.floor(Date.now() / 1000) - 300) * 1000),
        );
        return payload;
      } catch (error) {
        const retryAttempt = options?.retryAttempt || 0;
        const retryOnFailure = cloudFrontCookieRequests.get(requestKey)?.retryOnFailure ?? options?.retryOnFailure !== false;
        if (retryOnFailure && retryAttempt < 2) {
          scheduleCloudFrontCookieRefresh(url, 10000, retryAttempt + 1);
        }
        throw error;
      } finally {
        cloudFrontCookieRequests.delete(requestKey);
      }
    })();
    pending = { url, promise: request, retryOnFailure: options?.retryOnFailure !== false };
    cloudFrontCookieRequests.set(requestKey, pending);
  }
  if (options?.retryOnFailure === false) pending.retryOnFailure = false;
  const payload = await pending.promise;
  return {
    url: url && url !== pending.url ? url : payload.url || url,
    expiresAt: payload.expiresAt,
    resource: payload.resource || '',
  };
};

export const postCreateMultiPartUpload = async (videoId: string, videofeedId: string, numParts: number) => {
  return await axiosInstance.post(API_ROUTES.VIDEOFEED_CREATE_MULTIPART_UPLOAD, {
    videoId,
    videofeedId,
    numberOfParts: numParts,
  });
};

export const postCreateAdditionalAngleUpload = async (data: {
  videofeedId: string;
  name?: string;
  size: number;
  quality: number;
  duration?: number;
  numberOfParts: number;
}) => {
  return await axiosInstance.post(API_ROUTES.VIDEOFEED_UPLOAD_ADDITIONAL_ANGLE, data);
};

export const postUploadChunk = async (chunk: File | Blob, uploadId: string, partNumber: number) => {
  const formData = new FormData();
  formData.append('chunk', chunk);
  formData.append('uploadId', uploadId);
  formData.append('partNumber', partNumber.toString());

  const response = await axiosInstance.post(API_ROUTES.VIDEOFEED_UPLOAD_CHUNK, formData, {
    headers: {
      'Content-Type': 'multipart/form-data', // Set content type to multipart/form-data
    },
  });

  return response.data;
};

export const postUploadChunkByPresignedUrl = async (chunk: File | Blob, presignedUrl: string, signal?: AbortSignal) => {
  try {
    const res = await fetch(presignedUrl, {
      method: 'PUT',
      body: chunk,
      signal,
    });
    const headers = res.headers;
    const eTag = headers.get('ETag');

    if (!eTag) {
      throw eTag;
    }
    return { ...res, ETag: eTag };
  } catch (err) {
    console.log('ERR postUploadChunkByPresignedUrl', err);
    throw err;
  }
};

export const postRegeneratePresignedUrls = async (uploadId: string, expiredPart: number, totalParts: number) => {
  const res = await axiosInstance.post(API_ROUTES.REGENERATE_PRESIGNED_URLS, {
    uploadId,
    expiredPart,
    totalParts,
  });
  return res;
};

export const postCompleteMultiPartUploadChunks = async (uploadId: string, chunks: any) => {
  console.log(chunks, 'chunks');
  return await axiosInstance.post(API_ROUTES.VIDEOFEED_COMPLETE_MULTIPART_UPLOAD_CHUNKS, {
    uploadId,
    chunks,
  });
};

export const postCompleteAdditionalAngleUpload = async (uploadId: string, chunks: any) => {
  return await axiosInstance.post(API_ROUTES.VIDEOFEED_COMPLETE_ADDITIONAL_ANGLE_UPLOAD, {
    uploadId,
    chunks,
  });
};

export const postCancelAdditionalAngleUpload = async (uploadId: string) => {
  return await axiosInstance.post(API_ROUTES.VIDEOFEED_CANCEL_ADDITIONAL_ANGLE_UPLOAD, {
    uploadId,
  });
};

export const postUploadMetaData = async (zip: File | Blob, videofeedId: string) => {
  const formData = new FormData();
  formData.append('file', zip);
  formData.append('videofeedId', videofeedId);

  const response = await axiosInstance.post(API_ROUTES.VIDEOFEED_UPLOAD_METADATA, formData, {
    headers: {
      'Content-Type': 'multipart/form-data', // Set content type to multipart/form-data
    },
  });

  return response.data;
};

export const postUploadJSON = async (
  rawJsonFile: File | Blob,
  videofeedId: string,
  isXml: boolean,
  options?: { allContent?: boolean; emptyTimeline?: boolean },
) => {
  const formData = new FormData();
  formData.append(isXml ? 'xml' : 'json', rawJsonFile);
  const params = new URLSearchParams();
  if (options?.allContent) params.set('allContent', 'true');
  if (options?.emptyTimeline) params.set('emptyTimeline', 'true');
  const query = params.toString();
  return axiosInstance.post(`${API_ROUTES.VIDEOFEED_UPLOAD_JSON}/${videofeedId}${query ? `?${query}` : ''}`, formData);
};

export const postCreateEmptyTimeline = (videofeedId: string, timelineName: string, options?: { allContent?: boolean }) => {
  const fileName = timelineName.trim().replace(/\.json$/i, '') || 'Empty Timeline';
  const file = new File([JSON.stringify({ rows: [], row_count: 0, uuid_timeline: '', data_version: 2 })], `${fileName}.json`, {
    type: 'application/json',
  });
  return postUploadJSON(file, videofeedId, false, { ...options, emptyTimeline: true });
};
